import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AddIcon from "@mui/icons-material/Add";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RefreshIcon from "@mui/icons-material/Refresh";
import type { AlertColor } from "@mui/material/Alert/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  type PlaidLinkOnSuccess,
  type PlaidLinkOnSuccessMetadata,
  usePlaidLink,
} from "react-plaid-link";

import useQueryClient from "../../api/query";
import { savePlaidOAuthState } from "./plaidOAuthStorage";
import {
  clearPendingMappingConnections,
  loadPendingMappingConnections,
  savePendingMappingConnections,
} from "./plaidPendingMapping";

interface BankConnectionsTabProps {
  connections: PlaidConnectionMessage[];
  loading: boolean;
  onDisconnect: () => void;
  onError: (msg: string) => void;
  practices: PracticeMessage[];
}

// Select value meaning "don't reconcile this account".
const SKIP = "__skip__";

function accountLabel(conn: PlaidConnectionMessage): string {
  const name = conn.accountName || "Account";
  return conn.accountMask ? `${name} ****${conn.accountMask}` : name;
}

function StatusChip({ status }: { status: string }) {
  if (status === "ACTIVE") {
    return (
      <Chip
        label="Active"
        size="small"
        sx={{
          backgroundColor: "#ECFDF3",
          color: "#027A48",
          fontWeight: 500,
        }}
      />
    );
  }
  if (status === "PENDING_MAPPING") {
    return (
      <Chip
        label="Awaiting Mapping"
        size="small"
        color="info"
        sx={{ fontWeight: 500 }}
      />
    );
  }
  return (
    <Chip
      label="Needs Reconnection"
      size="small"
      color="warning"
      sx={{ fontWeight: 500 }}
    />
  );
}

function EmptyState() {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "4rem 2rem",
        textAlign: "center",
      }}
    >
      <AccountBalanceIcon
        sx={{ fontSize: 48, color: "#D0D5DD", marginBottom: "1rem" }}
      />
      <Typography
        variant="h6"
        sx={{ color: "#344054", marginBottom: "0.5rem" }}
      >
        No Bank Connections
      </Typography>
      <Typography variant="body2" sx={{ color: "#667085" }}>
        Connect your bank accounts to automate payment reconciliation. Click
        &quot;+ Connect Bank&quot; above to get started.
      </Typography>
    </Box>
  );
}

type PlaidConflictDetail = {
  detail: string;
  code: string;
  connection_id: string;
};

function PlaidLinkWidget({
  onSuccess,
  onExit,
  onError,
  onNeedsReauth,
}: {
  onSuccess: () => void;
  onExit: () => void;
  onError: (msg: string) => void;
  onNeedsReauth: (connectionId: string) => void;
}) {
  const queryClient = useQueryClient();
  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const onNeedsReauthRef = useRef(onNeedsReauth);
  onNeedsReauthRef.current = onNeedsReauth;
  const [linkToken, setLinkToken] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchToken = async () => {
      try {
        const { linkToken: token } =
          await queryClientRef.current.createPlaidLinkToken();
        if (!cancelled) {
          // Persisted so /plaid-oauth can resume Link after the OAuth redirect.
          savePlaidOAuthState({ linkToken: token, mode: "connect" });
          setLinkToken(token);
        }
      } catch {
        if (!cancelled) {
          onErrorRef.current(
            "Failed to initialize bank connection. Please try again.",
          );
        }
      }
    };
    fetchToken();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSuccess: PlaidLinkOnSuccess = useCallback(
    async (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => {
      try {
        const connections =
          await queryClientRef.current.exchangePlaidPublicToken(publicToken, {
            institutionId: metadata.institution?.institution_id,
            institutionName: metadata.institution?.name,
          });
        // Persist chosen accounts so the mapping modal survives the refetch/remount.
        savePendingMappingConnections(connections);
        onSuccessRef.current();
      } catch (err: unknown) {
        const response = (
          err as {
            response?: {
              status?: number;
              data?: { detail?: string | PlaidConflictDetail };
            };
          }
        )?.response;
        const detail = response?.status === 409 ? response.data?.detail : null;
        // The login is already linked but broken — update mode repairs it in
        // place, so hand off instead of dead-ending the user here.
        if (
          detail &&
          typeof detail === "object" &&
          detail.code === "NEEDS_REAUTH"
        ) {
          onNeedsReauthRef.current(detail.connection_id);
          return;
        }
        onErrorRef.current(
          detail
            ? "This bank login is already connected. Add accounts from the existing connection instead."
            : "Failed to connect bank account. Please try again.",
        );
      }
    },
    [],
  );

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: handleSuccess,
    onExit,
  });

  useEffect(() => {
    if (ready) {
      open();
    }
  }, [ready, open]);

  return null;
}

function ReauthLinkWidget({
  connectionId,
  onSuccess,
  onExit,
  onError,
}: {
  connectionId: string;
  onSuccess: () => void;
  onExit: () => void;
  onError: (msg: string) => void;
}) {
  const queryClient = useQueryClient();
  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const [linkToken, setLinkToken] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchToken = async () => {
      try {
        const { linkToken: token } =
          await queryClientRef.current.createPlaidReauthLinkToken(connectionId);
        if (!cancelled) {
          savePlaidOAuthState({
            linkToken: token,
            mode: "reauth",
            connectionId,
          });
          setLinkToken(token);
        }
      } catch {
        if (!cancelled) {
          onErrorRef.current(
            "Failed to start re-authentication. Please try again.",
          );
        }
      }
    };
    fetchToken();
    return () => {
      cancelled = true;
    };
  }, [connectionId]);

  const handleSuccess: PlaidLinkOnSuccess = useCallback(async () => {
    try {
      await queryClientRef.current.completePlaidReauth(connectionId);
      onSuccessRef.current();
    } catch {
      onErrorRef.current(
        "Re-authentication succeeded but status update failed. Please refresh.",
      );
    }
  }, [connectionId]);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: handleSuccess,
    onExit,
  });

  useEffect(() => {
    if (ready) {
      open();
    }
  }, [ready, open]);

  return null;
}

function AddAccountsLinkWidget({
  connectionId,
  onSuccess,
  onExit,
  onError,
}: {
  connectionId: string;
  onSuccess: (added: PlaidConnectionMessage[]) => void;
  onExit: () => void;
  onError: (msg: string) => void;
}) {
  const queryClient = useQueryClient();
  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const [linkToken, setLinkToken] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchToken = async () => {
      try {
        // Update-mode token: add accounts without disconnecting (no duplicate txns).
        const { linkToken: token } =
          await queryClientRef.current.addAccountsLinkToken(connectionId);
        if (!cancelled) {
          savePlaidOAuthState({
            linkToken: token,
            mode: "add-accounts",
            connectionId,
          });
          setLinkToken(token);
        }
      } catch {
        if (!cancelled) {
          onErrorRef.current(
            "Failed to start adding accounts. Please try again.",
          );
        }
      }
    };
    fetchToken();
    return () => {
      cancelled = true;
    };
  }, [connectionId]);

  const handleSuccess: PlaidLinkOnSuccess = useCallback(async () => {
    try {
      // New accounts come back PENDING_MAPPING; persist for the mapping modal.
      const added =
        await queryClientRef.current.completeAddAccounts(connectionId);
      savePendingMappingConnections(added);
      onSuccessRef.current(added);
    } catch {
      onErrorRef.current("Failed to add accounts. Please try again.");
    }
  }, [connectionId]);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: handleSuccess,
    onExit,
  });

  useEffect(() => {
    if (ready) {
      open();
    }
  }, [ready, open]);

  return null;
}

export function ConnectBankButton({
  onSuccess,
  updateSnackBar,
}: {
  onSuccess: () => void;
  updateSnackBar: (snackbar: { severity: AlertColor; message: string }) => void;
}) {
  const [connecting, setConnecting] = useState(false);
  const [reauthConnectionId, setReauthConnectionId] = useState<string | null>(
    null,
  );

  return (
    <>
      <Button
        variant="contained"
        onClick={() => setConnecting(true)}
        disabled={connecting}
        startIcon={
          connecting ? <CircularProgress size={16} color="inherit" /> : null
        }
      >
        {connecting ? "Connecting..." : "+ Connect Bank"}
      </Button>
      {connecting && (
        <PlaidLinkWidget
          onSuccess={() => {
            setConnecting(false);
            onSuccess();
          }}
          onExit={() => setConnecting(false)}
          onError={(msg) => {
            setConnecting(false);
            updateSnackBar({ severity: "error", message: msg });
          }}
          onNeedsReauth={(connectionId) => {
            setConnecting(false);
            setReauthConnectionId(connectionId);
            updateSnackBar({
              severity: "info",
              message:
                "This bank login is already connected but needs reconnecting — continue to restore it.",
            });
          }}
        />
      )}
      {reauthConnectionId && (
        <ReauthLinkWidget
          connectionId={reauthConnectionId}
          onSuccess={() => {
            setReauthConnectionId(null);
            updateSnackBar({
              severity: "success",
              message: "Bank connection restored.",
            });
            onSuccess();
          }}
          onExit={() => setReauthConnectionId(null)}
          onError={(msg) => {
            setReauthConnectionId(null);
            updateSnackBar({ severity: "error", message: msg });
          }}
        />
      )}
    </>
  );
}

function MappingModal({
  connections,
  practices,
  onComplete,
  onDismiss,
}: {
  connections: PlaidConnectionMessage[];
  practices: PracticeMessage[];
  onComplete: (remaining: PlaidConnectionMessage[], errorMsg?: string) => void;
  onDismiss: () => void;
}) {
  const queryClient = useQueryClient();
  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;
  const [layer, setLayer] = useState<"map" | "confirm">("map");
  const [assignments, setAssignments] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Every account needs a decision: a practice OR skip ("don't reconcile").
  const allDecided = connections.every((c) => assignments[c.id]);
  const assignedConnections = connections.filter(
    (c) => assignments[c.id] && assignments[c.id] !== SKIP,
  );
  const skippedCount = connections.filter(
    (c) => assignments[c.id] === SKIP,
  ).length;
  const practiceName = (id: string) =>
    practices.find((p) => p.wieldyId === id)?.displayName ?? "-";

  const handleConfirm = async () => {
    setSubmitting(true);
    const results = await Promise.all(
      connections.map(async (conn) => {
        const decision = assignments[conn.id];
        try {
          if (decision === SKIP) {
            await queryClientRef.current.skipPlaidConnection(conn.id);
          } else {
            await queryClientRef.current.mapPlaidConnection(conn.id, decision);
          }
          return { conn, ok: true, conflict: false };
        } catch (err: unknown) {
          const status = (err as { response?: { status?: number } })?.response
            ?.status;
          return { conn, ok: false, conflict: status === 409 };
        }
      }),
    );
    setSubmitting(false);
    const failed = results.filter((r) => !r.ok).map((r) => r.conn);
    if (failed.length === 0) {
      onComplete([]);
      return;
    }
    setLayer("map");
    onComplete(
      failed,
      results.some((r) => !r.ok && r.conflict)
        ? "Some accounts are already connected to a practice. Please review and try again."
        : "Couldn't finish mapping some accounts. Please try again.",
    );
  };

  return (
    <Dialog open maxWidth="sm" fullWidth disableEscapeKeyDown>
      {layer === "map" ? (
        <>
          <DialogTitle>Assign accounts to practices</DialogTitle>
          <DialogContent>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Account</TableCell>
                  <TableCell>Practice</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {connections.map((conn) => (
                  <TableRow key={conn.id}>
                    <TableCell>{accountLabel(conn)}</TableCell>
                    <TableCell sx={{ width: "50%" }}>
                      <Select
                        size="small"
                        value={assignments[conn.id] ?? ""}
                        onChange={(e) =>
                          setAssignments((prev) => ({
                            ...prev,
                            [conn.id]: e.target.value,
                          }))
                        }
                        fullWidth
                        displayEmpty
                      >
                        <MenuItem value="" disabled>
                          Select a practice
                        </MenuItem>
                        <MenuItem value={SKIP}>
                          Skip (don&apos;t reconcile)
                        </MenuItem>
                        {practices.map((p) => (
                          <MenuItem key={p.wieldyId} value={p.wieldyId}>
                            {p.displayName}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </DialogContent>
          <DialogActions>
            <Button onClick={onDismiss}>Finish later</Button>
            <Button
              variant="contained"
              onClick={() => setLayer("confirm")}
              disabled={!allDecided}
            >
              Next
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle>Confirm mapping</DialogTitle>
          <DialogContent>
            <Table size="small">
              <TableBody>
                {assignedConnections.map((conn) => (
                  <TableRow key={conn.id}>
                    <TableCell>{accountLabel(conn)}</TableCell>
                    <TableCell>{practiceName(assignments[conn.id])}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {assignedConnections.length > 0 && (
              <Typography variant="body2" sx={{ color: "#667085", mt: 2 }}>
                These accounts will start reconciling once confirmed.
              </Typography>
            )}
            {skippedCount > 0 && (
              <Typography variant="body2" sx={{ color: "#667085", mt: 1 }}>
                {skippedCount} account{skippedCount === 1 ? "" : "s"} skipped
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLayer("map")} disabled={submitting}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={submitting}
              startIcon={
                submitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : null
              }
            >
              Confirm
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

// Confirms an action that disconnects, or that re-authorizes the whole login.
function ImpactConfirmDialog({
  title,
  message,
  affected,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  title: string;
  message: string;
  affected: string[];
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Dialog open maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: "#344054" }}>
          {message}
        </Typography>
        {affected.length > 0 && (
          <Box component="ul" sx={{ mt: 1.5, mb: 0, pl: 3 }}>
            {affected.map((label) => (
              <Box component="li" key={label}>
                <Typography variant="body2" sx={{ color: "#667085" }}>
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function BankConnectionsTab({
  connections,
  loading,
  onDisconnect,
  onError,
  practices,
}: BankConnectionsTabProps) {
  const queryClient = useQueryClient();
  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;
  const [disconnecting, setDisconnecting] = useState<string | null>(null);
  const [reauthConnectionId, setReauthConnectionId] = useState<string | null>(
    null,
  );
  const [addAccountsConnectionId, setAddAccountsConnectionId] = useState<
    string | null
  >(null);
  const [confirm, setConfirm] = useState<{
    type: "reauth" | "disconnect" | "addAccounts";
    conn: PlaidConnectionMessage;
  } | null>(null);
  const [menu, setMenu] = useState<{
    anchor: HTMLElement;
    conn: PlaidConnectionMessage;
  } | null>(null);
  const [pendingConnections, setPendingConnections] = useState<
    PlaidConnectionMessage[]
  >(() => loadPendingMappingConnections());
  const [mappingDismissed, setMappingDismissed] = useState(false);

  // Surface /exchange accounts for both the direct-connect and OAuth-return flows.
  // biome-ignore lint/correctness/useExhaustiveDependencies: dropping connections would change when this runs
  useEffect(() => {
    const stored = loadPendingMappingConnections();
    if (stored.length > 0) {
      setPendingConnections(stored);
      setMappingDismissed(false);
    }
  }, [connections]);

  const practiceMap = practices.reduce(
    (acc, p) => {
      acc[p.wieldyId] = p.displayName;
      return acc;
    },
    {} as Record<string, string>,
  );

  const handleDisconnect = async (connectionId: string) => {
    setDisconnecting(connectionId);
    try {
      await queryClientRef.current.deletePlaidConnection(connectionId);
      onDisconnect();
    } catch {
      onError("Failed to disconnect bank account. Please try again.");
    } finally {
      setDisconnecting(null);
    }
  };

  // Live accounts on this row's login (one Item), including itself.
  const loginGroup = (conn: PlaidConnectionMessage) =>
    connections.filter(
      (c) => c.itemId === conn.itemId && c.syncStatus !== "DISCONNECTED",
    );

  // Confirm when the login has siblings; reconnect restores all of them.
  const startReauth = (conn: PlaidConnectionMessage) => {
    if (loginGroup(conn).length > 1) {
      setConfirm({ type: "reauth", conn });
    } else {
      setReauthConnectionId(conn.id);
    }
  };

  const startDisconnect = (conn: PlaidConnectionMessage) =>
    setConfirm({ type: "disconnect", conn });

  const handleMappingComplete = (
    remaining: PlaidConnectionMessage[],
    errorMsg?: string,
  ) => {
    if (remaining.length === 0) {
      clearPendingMappingConnections();
      setPendingConnections([]);
      setMappingDismissed(false);
      onDisconnect();
      return;
    }
    savePendingMappingConnections(remaining);
    setPendingConnections(remaining);
    if (errorMsg) {
      onError(errorMsg);
    }
  };

  const showMapping = pendingConnections.length > 0 && !mappingDismissed;

  let confirmDialog: React.ReactNode = null;
  if (confirm) {
    const bank = confirm.conn.institutionName || "this bank";
    if (confirm.type === "reauth") {
      confirmDialog = (
        <ImpactConfirmDialog
          title="Reconnect this bank login?"
          message={`Reconnecting re-authenticates every account on your ${bank} login, not just this one. Any that need reconnection will resume syncing:`}
          affected={loginGroup(confirm.conn).map(accountLabel)}
          confirmLabel="Reconnect"
          onConfirm={() => {
            setReauthConnectionId(confirm.conn.id);
            setConfirm(null);
          }}
          onCancel={() => setConfirm(null)}
        />
      );
    } else if (confirm.type === "addAccounts") {
      confirmDialog = (
        <ImpactConfirmDialog
          title="Add accounts to this bank login?"
          message={`Adding accounts re-authorizes your ${bank} login. You must finish the bank's approval screen — if you close it, or the bank declines, these accounts stop syncing and have to be reconnected from scratch:`}
          affected={loginGroup(confirm.conn).map(accountLabel)}
          confirmLabel="Continue"
          onConfirm={() => {
            setAddAccountsConnectionId(confirm.conn.id);
            setConfirm(null);
          }}
          onCancel={() => setConfirm(null)}
        />
      );
    } else {
      const lastOnLogin = loginGroup(confirm.conn).length === 1;
      confirmDialog = (
        <ImpactConfirmDialog
          title={
            lastOnLogin
              ? "Disconnect this bank login?"
              : "Disconnect this account?"
          }
          message={
            lastOnLogin
              ? `This is the only account still connected on your ${bank} login, so disconnecting it removes the entire login from Plaid. You'll need to reconnect from scratch to use it again.`
              : `Disconnecting stops syncing for this account. Your other accounts on the ${bank} login stay connected.`
          }
          affected={[]}
          confirmLabel="Disconnect"
          onConfirm={() => {
            handleDisconnect(confirm.conn.id);
            setConfirm(null);
          }}
          onCancel={() => setConfirm(null)}
        />
      );
    }
  }

  let body: React.ReactNode;
  if (loading) {
    body = (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          padding: "4rem",
        }}
      >
        <CircularProgress />
      </Box>
    );
  } else if (connections.length === 0) {
    body = <EmptyState />;
  } else {
    body = (
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Institution</TableCell>
              <TableCell>Account</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Practice</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Connected</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {connections.map((conn) => {
              const busy =
                disconnecting === conn.id ||
                addAccountsConnectionId === conn.id;
              return (
                <TableRow key={conn.id}>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <AccountBalanceIcon
                        sx={{ color: "#667085", fontSize: 20 }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {conn.institutionName || "Unknown Institution"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {conn.accountName || "Account"}
                      {conn.accountMask && (
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{ color: "#667085", ml: 0.5 }}
                        >
                          ****{conn.accountMask}
                        </Typography>
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ textTransform: "capitalize" }}
                    >
                      {conn.accountSubtype || conn.accountType || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {(conn.practiceId && practiceMap[conn.practiceId]) || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <StatusChip status={conn.syncStatus} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: "#667085" }}>
                      {new Date(conn.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 0.5,
                      }}
                    >
                      {conn.syncStatus === "NEEDS_REAUTH" && (
                        <Tooltip title="Reconnect">
                          <IconButton
                            size="small"
                            onClick={() => startReauth(conn)}
                            disabled={reauthConnectionId === conn.id}
                          >
                            {reauthConnectionId === conn.id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <RefreshIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="More actions">
                        <IconButton
                          size="small"
                          onClick={(e) =>
                            setMenu({ anchor: e.currentTarget, conn })
                          }
                          disabled={busy}
                        >
                          {busy ? (
                            <CircularProgress size={16} />
                          ) : (
                            <MoreVertIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      {body}
      {reauthConnectionId && (
        <ReauthLinkWidget
          connectionId={reauthConnectionId}
          onSuccess={() => {
            setReauthConnectionId(null);
            onDisconnect();
          }}
          onExit={() => setReauthConnectionId(null)}
          onError={(msg) => {
            setReauthConnectionId(null);
            onError(msg);
          }}
        />
      )}
      {addAccountsConnectionId && (
        <AddAccountsLinkWidget
          connectionId={addAccountsConnectionId}
          onSuccess={(added) => {
            setAddAccountsConnectionId(null);
            // Show new accounts in the mapping modal; refetch to update rows.
            setPendingConnections(added);
            setMappingDismissed(false);
            onDisconnect();
          }}
          onExit={() => setAddAccountsConnectionId(null)}
          onError={(msg) => {
            setAddAccountsConnectionId(null);
            onError(msg);
          }}
        />
      )}
      {showMapping && (
        <MappingModal
          connections={pendingConnections}
          practices={practices}
          onComplete={handleMappingComplete}
          onDismiss={() => setMappingDismissed(true)}
        />
      )}
      {menu && (
        <Menu anchorEl={menu.anchor} open onClose={() => setMenu(null)}>
          {menu.conn.syncStatus === "ACTIVE" && (
            <MenuItem
              onClick={() => {
                setConfirm({ type: "addAccounts", conn: menu.conn });
                setMenu(null);
              }}
            >
              <ListItemIcon>
                <AddIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Add accounts</ListItemText>
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              startDisconnect(menu.conn);
              setMenu(null);
            }}
          >
            <ListItemIcon>
              <LinkOffIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Disconnect</ListItemText>
          </MenuItem>
        </Menu>
      )}
      {confirmDialog}
    </Box>
  );
}
